import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Provider, useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState, store } from "./redux/store";
import { listenForAuthChanges, listenToUserData } from "./redux/authSlice";
import Profile from "./AUTH/profile";
import logoImg from "./assets/app/logo.png";

import Checkout from "./APP/DASHBOARD/Checkout";
import CustomerOrders from "./APP/CUSTOMER/customerorders";
import OwnerDashboard from "./APP/DASHBOARD/ownerdashboard";

import Shop from "./APP/DASHBOARD/Shop";
import { CurrencyProvider } from "./GLOBAL/Currencies";
import { LanguageProvider } from "./GLOBAL/Languages";
import AccountSecurity from "./AUTH/accountsecurity";
import EditProfile from "./AUTH/editprofile";
import ComingSoon from "./GLOBAL/ComingSoon";
import Authentication from "./AUTH/authentication";
import Makeup from "./APP/DASHBOARD/Makeup";
import Explore from "./APP/DASHBOARD/Explore";



/* ═══════════════════════════ 3D LOADER: black, white and red ═══════════════════════════ */
const LOADER_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600&family=Jost:wght@400;500&display=swap');
.ld{position:fixed;inset:0;z-index:9999;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:64px;background:#000;color:#fff;overflow:hidden}
.ld *{box-sizing:border-box}
.ld-stage{position:relative;width:140px;height:140px;perspective:700px;display:flex;align-items:center;justify-content:center}
.ld-rig{position:relative;width:72px;height:72px;transform-style:preserve-3d;animation:ldSpin 4.2s linear infinite, ldBob 2.1s ease-in-out infinite}
.ld-cube,.ld-core{position:absolute;inset:0;transform-style:preserve-3d}
.ld-core{animation:ldSpinRev 3s linear infinite}
.ld-f{position:absolute;inset:0;border:1px solid rgba(255,255,255,.9);backface-visibility:visible}
.ld-cube .ld-f{background:linear-gradient(135deg,rgba(255,60,60,.92) 0%,rgba(225,0,0,.88) 45%,rgba(120,0,0,.92) 100%);
  box-shadow:inset 0 0 18px rgba(255,255,255,.35),inset 6px 6px 14px rgba(255,255,255,.25)}
.ld-cube .ld-f::after{content:"";position:absolute;top:8%;left:8%;width:46%;height:16%;border-radius:999px;background:linear-gradient(90deg,rgba(255,255,255,.85),rgba(255,255,255,0));transform:rotate(-24deg)}
.ld-core .ld-f{background:linear-gradient(135deg,#fff,#d9d9d9);border-color:#fff}
.ld-cube .ld-f:nth-child(1){transform:rotateY(0deg) translateZ(36px)}
.ld-cube .ld-f:nth-child(2){transform:rotateY(180deg) translateZ(36px)}
.ld-cube .ld-f:nth-child(3){transform:rotateY(90deg) translateZ(36px)}
.ld-cube .ld-f:nth-child(4){transform:rotateY(-90deg) translateZ(36px)}
.ld-cube .ld-f:nth-child(5){transform:rotateX(90deg) translateZ(36px)}
.ld-cube .ld-f:nth-child(6){transform:rotateX(-90deg) translateZ(36px)}
.ld-core{inset:22px}
.ld-core .ld-f:nth-child(1){transform:rotateY(0deg) translateZ(14px)}
.ld-core .ld-f:nth-child(2){transform:rotateY(180deg) translateZ(14px)}
.ld-core .ld-f:nth-child(3){transform:rotateY(90deg) translateZ(14px)}
.ld-core .ld-f:nth-child(4){transform:rotateY(-90deg) translateZ(14px)}
.ld-core .ld-f:nth-child(5){transform:rotateX(90deg) translateZ(14px)}
.ld-core .ld-f:nth-child(6){transform:rotateX(-90deg) translateZ(14px)}
.ld-shadow{position:absolute;bottom:-34px;left:50%;width:84px;height:16px;margin-left:-42px;border-radius:50%;
  background:radial-gradient(ellipse at center,rgba(225,0,0,.65) 0%,rgba(225,0,0,0) 70%);animation:ldShadow 2.1s ease-in-out infinite}
.ld-text{font-family:'Cormorant Garamond',serif;font-weight:600;font-size:13px;letter-spacing:.5em;padding-left:.5em;animation:ldPulse 1.8s ease-in-out infinite}
@keyframes ldSpin{from{transform:rotateX(-24deg) rotateY(0deg)}to{transform:rotateX(-24deg) rotateY(360deg)}}
@keyframes ldSpinRev{from{transform:rotateX(0deg) rotateY(0deg) rotateZ(0deg)}to{transform:rotateX(360deg) rotateY(-360deg) rotateZ(180deg)}}
@keyframes ldBob{0%,100%{margin-top:0}50%{margin-top:-14px}}
@keyframes ldShadow{0%,100%{transform:scale(1);opacity:.9}50%{transform:scale(.7);opacity:.5}}
@keyframes ldPulse{0%,100%{opacity:.4}50%{opacity:1}}
@media (prefers-reduced-motion:reduce){.ld-rig,.ld-core,.ld-shadow,.ld-text{animation:none}.ld-rig{transform:rotateX(-24deg) rotateY(35deg)}}
`;

const Loader = () => (
  <div className="ld" role="status" aria-live="polite">
    <style>{LOADER_CSS}</style>
    <div className="ld-stage" aria-hidden="true">
      <div className="ld-rig">
        <div className="ld-cube"><i className="ld-f" /><i className="ld-f" /><i className="ld-f" /><i className="ld-f" /><i className="ld-f" /><i className="ld-f" /></div>
        <div className="ld-core"><i className="ld-f" /><i className="ld-f" /><i className="ld-f" /><i className="ld-f" /><i className="ld-f" /><i className="ld-f" /></div>
      </div>
      <span className="ld-shadow" />
    </div>
    <span className="ld-text">LOADING</span>
  </div>
);

/* ═══════════════════════════ ROUTING ═══════════════════════════ */
const Protected = () => {
  const user = useSelector((s: RootState) => s.auth.user);
  return user ? <Outlet /> : <Navigate to="/auth" replace />;
};


const AppRoutes = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, authLoaded } = useSelector((state: RootState) => state.auth);

 
   useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);
  
 

  useEffect(() => {
    dispatch(listenForAuthChanges());
  }, [dispatch]);

  
{/*
    useEffect(() => {
  if (user) {
    // 1 hour = 60 minutes × 60 seconds × 1000 ms = 3,600,000 ms
    dispatch(startInactivityWatcher({ timeoutMs: 60 * 60 * 1000 }));
  }
}, [dispatch, user]);
  
  
  */}




  


  if (!authLoaded) {
    return <AppLoadingScreen />;
  }



  const ProtectedSupportRoute = () => {
    const authUser = useSelector((state: RootState) => state.auth.user);
  
    if (!authUser) return <Navigate to="/AuthSwitch" replace />;
  
    const hasAccess = authUser.role?.includes("support") || authUser.role?.includes("admin");
  
    if (!hasAccess) return <Navigate to="/" replace />;
  
    return <Outlet />;
  };

  return (
    <>

    <Routes>
      {/* ✅ Publicly accessible pages/tabs */}
      <Route
  path="/"
  element={
    <>
    
  
        {/*


    
  <GeneralHomes/>  

    <HomeLive />     

      <WhatsNew /> 
        <Route index element={<Ahome />} />
    
       */}
    </>
  }
/>


   

        <Route path="/" element={<Outlet  />}>

<Route index element={<RootRedirect />} />

<Route path="/comingsoon" element={<ComingSoon />} />
      <Route path="/AuthSwitch" element={<Authentication />} />

        <Route path="/makeup" element={<Explore />} />



      <Route element={<Protected />}>
    
        <Route path="/profile/:userId" element={<Profile />} />
                <Route path="/editprofile/:userId" element={<EditProfile />} />
          <Route path="/account-security/:userId" element={<AccountSecurity />} />

        <Route path="/shop" element={<Shop />} />
        <Route path="/makeup" element={<Makeup />} />

        <Route path="/checkout" element={<Checkout />} />
        <Route path="/orders" element={<CustomerOrders />} />
        <Route path="/owner" element={<OwnerDashboard />} />
      </Route>
  


    <Route path="*" element={<Navigate to={user ? "/" : "/authswitch"} replace />} />
        </Route>

    
    </Routes>
</>
);};


const BOOT_RAMP = [
  "#2B2018", "#ff0062", "#8c0000", "#ff0048", "#FDC301",
  "#00ff9d", "#FD0201", "#FF5450", "#B50000",
];

const BOOT_CSS = `
  @keyframes aeBootSpin { to { transform: rotate(360deg); } }
  @keyframes aeBootBounce {
    0%, 80%, 100% { transform: translateY(0) scale(0.72); }
    40%           { transform: translateY(-22px) scale(1); }
  }
  .ae-boot-dot { animation: aeBootBounce 1.1s cubic-bezier(.34,1.56,.64,1) infinite; }
`;

const AppLoadingScreen: React.FC = () => {
  const CARD_COUNT = 5;
  const RADIUS = 34; // how far each petal card sits from center
  const CARD_SIZE = 84; // matches the logo's own width/height

  // 5 cards fanned around a center point like petals — front card (i=0)
  // sits centered and on top, the rest fan out behind it.
  const cards = Array.from({ length: CARD_COUNT }, (_, i) => {
    const angle = (360 / CARD_COUNT) * i - 90; // start at 12 o'clock
    const rad = (angle * Math.PI) / 180;
    const x = i === 0 ? 0 : Math.cos(rad) * RADIUS;
    const y = i === 0 ? 0 : Math.sin(rad) * RADIUS;
    return { x, y, z: CARD_COUNT - i, scale: i === 0 ? 1 : 0.72, delay: i * 0.15 };
  });

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 40,
        background: "#FFFBF1",
        zIndex: 9999,
      }}
    >
      <style>{BOOT_CSS}</style>

      {/* logo card, stacked 5x in a flower fan */}
      <div
        style={{
          position: "relative",
          width: CARD_SIZE + RADIUS * 2,
          height: CARD_SIZE + RADIUS * 2,
        }}
      >
        {cards.map(({ x, y, z, scale, delay }, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) scale(${scale})`,
              zIndex: z,
              display: "inline-flex",
              borderRadius: 28,
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: -3,
                borderRadius: 31,
                background:
                  "linear-gradient(90deg,#2B2018,#566B2C,#789243,#FDC301,#FD0201,#566B2C,#2B2018)",
                backgroundSize: "300% 100%",
                animation: `aeBootSpin 3s linear infinite`,
                animationDelay: `${delay}s`,
              }}
            />
            <div
              style={{
                position: "relative",
                background: "#FFFFFF",
                borderRadius: 26,
                padding: 14,
                display: "inline-flex",
                boxShadow: "0 12px 30px rgba(43,32,24,0.18)",
              }}
            >
              <img
                src={logoImg}
                alt="Alovable"
                style={{ width: 84, height: 84, objectFit: "contain", display: "block" }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* 9 spheres, plain bouncing row */}
  {/* 9 spheres, plain bouncing row — growing small → biggest left to right */}
{/* 9 spheres, small → biggest — sized with clamp() so they scale down
    together on narrow viewports instead of overflowing off-screen */}
<div
  style={{
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    justifyContent: "center",
    gap: "clamp(6px, 2vw, 14px)",
    maxWidth: "92vw",
    padding: "0 12px",
  }}
>
  {BOOT_RAMP.map((color, i) => {
    const MIN_SIZE = 18;
    const MAX_SIZE = 180;
    const size = MIN_SIZE + ((MAX_SIZE - MIN_SIZE) * i) / (BOOT_RAMP.length - 1);

    // Same growth curve, but capped by a vw-based ceiling so on a narrow
    // screen every sphere shrinks proportionally instead of the biggest
    // one alone busting past the viewport edge.
    const vwSize = 5 + (size / MAX_SIZE) * 16; // ~5vw → ~21vw across the ramp

    return (
      <div
        key={i}
        className="ae-boot-dot"
        style={{
          width: `clamp(${MIN_SIZE}px, ${vwSize}vw, ${size}px)`,
          height: `clamp(${MIN_SIZE}px, ${vwSize}vw, ${size}px)`,
          aspectRatio: "1 / 1",
          borderRadius: "50%",
          background: `radial-gradient(circle at 34% 26%, rgba(255,255,255,.85) 0%, rgba(255,255,255,.12) 34%, transparent 58%), ${color}`,
          boxShadow: `inset 0 2px 0 rgba(255,255,255,.4), inset 0 -3px 0 rgba(0,0,0,.25), 0 6px 0 rgba(0,0,0,.18), 0 10px 18px rgba(0,0,0,.22)`,
          animationDelay: `${i * 0.09}s`,
        }}
      />
    );
  })}
</div>
    </div>
  );
};

const RootRedirect: React.FC = () => {
  const { user } = useSelector((s: RootState) => s.auth);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!user) return;
    const timer = setTimeout(() => setShow(true), 3000);
    return () => clearTimeout(timer);
  }, [user]);

  if (show) return <ComingSoon />;
  return <ComingSoon />;
};

const App = () => (
  <Provider store={store}>


    <Router>






  <CurrencyProvider>

<LanguageProvider 
  apiKey={process.env.REACT_APP_GOOGLE_TRANSLATE_API_KEY!}
>

        <AppRoutes />
 


 </LanguageProvider>

       </CurrencyProvider>

    </Router>

  </Provider>
);

export default App;